import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Field, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import ContactEmailDetails from "components/ContactEmailDetails";
import CopyableValue from "components/CopyableValue";
import { CampaignList } from "components/sidebar/sections/EntityCampaignsPanel";
import EntitySection from "components/sidebar/sections/EntitySection";
import {
  CustomFieldsPanelSection,
  PanelField,
  PanelSection,
} from "components/sidebar/sections/InfoPanel";
import TagsPanelSection from "components/sidebar/sections/TagsPanelSection";
import CONFIG from "Config";
import CONSTANTS from "constants/Constants";
import {
  hasInvalidEmailDomain,
  sortEmailAddressesByPrimary,
} from "helpers/contacts";
import { formatError } from "helpers/formatError";
import getPhoneType from "helpers/PhoneTypes";
import ContactService from "services/Contact";
import UserService from "services/User";
import useAuthStore from "stores/useAuthStore";

// Inactive campaigns are hidden unless the user opts in; the preference persists
// per user under the "contacts" sidebar scope, shared with the classic rail.
const ContactCampaigns = ({ contactId, isRecordLoading }) => {
  const [showInactive, setShowInactive] = useState(
    () =>
      useAuthStore.getState().userAuth?.sidebar_settings?.contacts
        ?.show_inactive_campaigns === true
  );

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ["contact", contactId, "campaigns"],
    queryFn: () => ContactService.getCampaigns(contactId),
    enabled: !!contactId,
  });

  const showInactiveSetting = useMutation({
    mutationFn: checked =>
      UserService.updateUserSidebarSettings(
        "contacts",
        "show_inactive_campaigns",
        checked
      ),
    onSuccess: (_data, checked) => setShowInactive(checked),
    onError: err =>
      toast.error(formatError(err, "Could not save the campaign setting")),
  });

  const visible = showInactive
    ? campaigns
    : campaigns.filter(
        ({ campaign_status_type }) =>
          campaign_status_type === CONSTANTS.CAMPAIGN_STATUS_TYPES.ACTIVE
      );

  return (
    <PanelSection title="Campaigns" isLoading={isRecordLoading || isLoading}>
      <Field orientation="horizontal">
        <Switch
          id="show-inactive-campaigns"
          checked={showInactive}
          disabled={showInactiveSetting.isPending}
          onCheckedChange={checked => showInactiveSetting.mutate(checked)}
        />
        <FieldLabel
          htmlFor="show-inactive-campaigns"
          className="tw:text-xs tw:font-normal"
        >
          Inactive campaigns
        </FieldLabel>
      </Field>
      {visible.length > 0 ? (
        <CampaignList campaigns={visible} />
      ) : (
        <span className="tw:text-xs tw:text-sidebar-foreground/60">None</span>
      )}
    </PanelSection>
  );
};

// Read-only information for the contact detail rail. Tags stay editable — the
// icon-only dropdown in each section title applies tags, and the header switch
// governs whether disabled tags appear in the lists and the dropdowns.
const ContactInfoPanel = ({
  contact,
  entity,
  showDisabledTags,
  onUpdate,
  isLoading = false,
  isEntityLoading = false,
}) => {
  const contactCustom = contact?.custom_fields;

  const emails = sortEmailAddressesByPrimary(contact?.email_addresses);
  const validEmails = emails.filter(email => !hasInvalidEmailDomain(email));
  const invalidEmails = emails.filter(hasInvalidEmailDomain);

  return (
    <>
      <PanelSection isLoading={isLoading}>
        <PanelField label="Name">{contact?.full_name}</PanelField>
        <PanelField label="ID">{contact?.id}</PanelField>
        <PanelField label="Email">
          {validEmails.map(emailAddress => (
            <CopyableValue key={emailAddress.id} value={emailAddress.email}>
              <ContactEmailDetails emailAddress={emailAddress} />
            </CopyableValue>
          ))}
        </PanelField>
        <PanelField label="Invalid domain emails">
          {invalidEmails.map(emailAddress => (
            <CopyableValue key={emailAddress.id} value={emailAddress.email}>
              <ContactEmailDetails emailAddress={emailAddress} destructive />
            </CopyableValue>
          ))}
        </PanelField>
        <PanelField label="Alternate Names">
          {contact.alternate_names.map(({ name }) => name)}
        </PanelField>
        <PanelField label="Department">{contact?.department_name}</PanelField>
        <PanelField label="Title">{contact?.title}</PanelField>
        {contact.phone_numbers
          .filter(({ enabled }) => enabled)
          .map(({ number, type, ext }) => (
            <PanelField key={number} label={getPhoneType(type)}>
              <CopyableValue value={number}>
                {ext ? `${number}x${ext}` : number}
              </CopyableValue>
            </PanelField>
          ))}
        <PanelField label="Email BCC">
          {CONFIG.CC_EMAIL ? (
            <CopyableValue value={CONFIG.CC_EMAIL} />
          ) : (
            "Not Configured"
          )}
        </PanelField>
        <PanelField label="Outreach Status">
          <div className="tw:flex tw:flex-wrap tw:gap-1">
            <Badge
              variant={contact?.emailable ? "success" : "destructive"}
              size="sm"
            >
              {contact?.emailable ? "Emailable" : "Not emailable"}
            </Badge>
            <Badge
              variant={contact?.callable ? "success" : "destructive"}
              size="sm"
            >
              {contact?.callable ? "Callable" : "Not callable"}
            </Badge>
          </div>
        </PanelField>
      </PanelSection>

      <TagsPanelSection
        title="Contact Tags"
        modelType="Contact"
        record={contact}
        isLoading={isLoading}
        showDisabledTags={showDisabledTags}
        onUpdate={onUpdate}
      />

      <CustomFieldsPanelSection
        title="Custom Fields"
        items={contactCustom}
        modelType="Contact"
        isLoading={isLoading}
      />

      {(isLoading || contact?.id) && (
        <ContactCampaigns contactId={contact?.id} isRecordLoading={isLoading} />
      )}

      {(isEntityLoading || entity?.id) && (
        <EntitySection
          entity={entity}
          isLoading={isEntityLoading}
          showDisabledTags={showDisabledTags}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

export default ContactInfoPanel;
